// Drop-card photos. One photo shows at a time, on a strip the photos sit along.
// The arrow, a horizontal trackpad swipe and a touch swipe step through them and
// loop. A swipe follows the finger, then a spring carries the strip to the photo
// the flick was heading for, so the release keeps its speed. The arrow and swatches
// slide the strip over CSS at any width; reduced motion changes it at once. Swatches with their own
// photo jump to it; swatches without one do nothing.
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const springs = new WeakMap();

const stopSpring = (card) => {
  cancelAnimationFrame(springs.get(card));
  springs.delete(card);
};

// Draws the strip: the photo at `index` sits at `track` px, the others follow it.
const render = (card, index, track, slide) => {
  const photos = [...card.querySelectorAll("[data-photo]")];
  const n = photos.length;
  photos.forEach((photo, i) => {
    let offset = (i - index + n) % n;
    if (offset > n / 2) offset -= n;
    const previous = Number(photo.dataset.offset ?? offset);
    const moves = slide && (Math.abs(offset) <= 1 || Math.abs(previous) <= 1);
    photo.style.transition = moves ? "" : "none";
    photo.style.transform = `translateX(calc(${offset * 100}% + ${track}px))`;
    photo.style.visibility = Math.abs(offset) <= 1 ? "visible" : "hidden";
    photo.dataset.offset = offset;
  });
  card.dataset.index = index;
  card.dataset.track = track;
  card.querySelectorAll(".swatch").forEach((swatch) => {
    swatch.setAttribute("aria-current", String(swatch.dataset.photoIndex === String(index)));
  });
};

const place = (card, index, animate) => {
  stopSpring(card);
  render(card, index, 0, animate && !reduceMotion.matches);
};

// Carries the strip from `from` px to rest, starting at `velocity` px/s. Critically
// damped, so it settles without bouncing.
const spring = (card, from, velocity, index) => {
  stopSpring(card);
  const k = 260;
  const c = 2 * Math.sqrt(k);
  let x = from;
  let v = velocity;
  let last = performance.now();
  const frame = (now) => {
    const dt = Math.min((now - last) / 1000, 1 / 30);
    last = now;
    v += (-k * x - c * v) * dt;
    x += v * dt;
    if (Math.abs(x) < 0.5 && Math.abs(v) < 4) {
      render(card, index, 0, false);
      springs.delete(card);
      return;
    }
    render(card, index, x, false);
    springs.set(card, requestAnimationFrame(frame));
  };
  springs.set(card, requestAnimationFrame(frame));
};

const step = (card, direction) => {
  const n = card.querySelectorAll("[data-photo]").length;
  if (n < 2) return;
  const current = Number(card.dataset.index || 0);
  place(card, (current + direction + n) % n, true);
};

document.querySelectorAll(".card__media").forEach((media) => {
  const card = media.closest(".card");
  if (card) render(card, 0, 0, false);
});

document.addEventListener("click", (event) => {
  const swatch = event.target.closest(".swatch[data-photo-index]");
  if (swatch) {
    const card = swatch.closest(".card");
    if (card) place(card, Number(swatch.dataset.photoIndex), true);
    return;
  }

  const next = event.target.closest(".card__next");
  if (next) step(next.closest(".card"), 1);
});

// A horizontal swipe on the trackpad steps the photo. Vertical scrolling is left
// alone, and a short lock stops one swipe from running through several photos.
let lastSwipe = 0;
document.addEventListener("wheel", (event) => {
  const media = event.target.closest(".card__media");
  if (!media) return;
  if (Math.abs(event.deltaX) < 12 || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
  if (Date.now() - lastSwipe < 500) return;
  const card = media.closest(".card");
  if (!card) return;
  lastSwipe = Date.now();
  step(card, event.deltaX > 0 ? 1 : -1);
}, { passive: true });

// Touch swipe. The drag starts only once the finger has moved sideways, so a
// vertical move still scrolls the page and a tap still opens the product.
let drag = null;

document.addEventListener("pointerdown", (event) => {
  if (event.pointerType !== "touch") return;
  const media = event.target.closest(".card__media");
  const card = media && media.closest(".card");
  if (!card) return;
  const n = card.querySelectorAll("[data-photo]").length;
  if (n < 2) return;
  stopSpring(card);
  const x0 = Number(card.dataset.track || 0);
  drag = {
    card, media, n, id: event.pointerId, width: media.clientWidth,
    startX: event.clientX, startY: event.clientY, x0,
    samples: [[performance.now(), event.clientX]], swiping: false,
  };
}, { passive: true });

document.addEventListener("pointermove", (event) => {
  if (!drag || event.pointerId !== drag.id) return;
  const dx = event.clientX - drag.startX;
  const dy = event.clientY - drag.startY;
  if (!drag.swiping) {
    if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
    if (Math.abs(dx) < 8) return;
    drag.swiping = true;
    drag.media.setPointerCapture(event.pointerId);
  }
  const now = performance.now();
  drag.samples.push([now, event.clientX]);
  drag.samples = drag.samples.filter(([t]) => now - t < 150);
  render(drag.card, Number(drag.card.dataset.index || 0), drag.x0 + dx, false);
}, { passive: true });

const release = (event) => {
  if (!drag || event.pointerId !== drag.id) return;
  const d = drag;
  drag = null;
  if (!d.swiping) return;

  // The tap that ends a swipe must not also open the product.
  const swallowClick = (click) => { click.preventDefault(); click.stopPropagation(); };
  d.media.addEventListener("click", swallowClick, { capture: true, once: true });
  setTimeout(() => d.media.removeEventListener("click", swallowClick, { capture: true }), 400);

  const track = Number(d.card.dataset.track || 0);
  const index = Number(d.card.dataset.index || 0);
  const first = d.samples[0];
  const last = d.samples[d.samples.length - 1];
  const seconds = Math.max((last[0] - first[0]) / 1000, 0.016);
  const velocity = (last[1] - first[1]) / seconds; // px/s

  // Where the flick would carry the strip if it coasted to rest (Apple's
  // deceleration projection, rate 0.998). Commit to the photo that is nearest.
  const projected = track + (velocity / 1000) * 0.998 / (1 - 0.998);
  const w = d.width;
  let direction = 0;
  if (projected < -w / 2) direction = 1;
  else if (projected > w / 2) direction = -1;

  if (direction === 0) {
    if (reduceMotion.matches) render(d.card, index, 0, false);
    else spring(d.card, track, velocity, index);
    return;
  }

  const next = (index + direction + d.n) % d.n;
  // Re-index so the photo under the finger stays put, then carry it to rest.
  render(d.card, next, track + direction * w, false);
  if (reduceMotion.matches) render(d.card, next, 0, false);
  else spring(d.card, track + direction * w, velocity, next);
};

document.addEventListener("pointerup", release);
document.addEventListener("pointercancel", release);
